/*
 * @format
 */

/* eslint no-unused-vars: ["error", { "argsIgnorePattern": "^_" }] */
/* eslint no-continue: 0 */

import {
  actionChannel,
  all,
  call,
  delay,
  put,
  race,
  take,
  select,
  spawn,
} from 'redux-saga/effects';
import uuid from 'uuid/v4';

import * as types from '../action-types';
import { flattenSchedule, unflattenSchedule } from '../schedule/schedule';
import { compareSchedule } from '../schedule/utils';
import { isMatchingZoneId } from '../utils/helpers';
import { GLOBAL_TIMEOUT } from '../utils/timeout';

import {
  getIsSettingRequestInProgress,
  getTimedDhw,
  getDeviceToken,
  getZoneType,
  isHaloDeviceServiceEnabled,
} from '../selectors';

import { getFrostProtectionSetpoint } from '../controls-selectors';

import { getActualSchedule } from '../selectors/schedule-selectors';

import * as messages from '../web_socket_messages/messages';

// check if we got the right result from the api
function* updateScheduleCheck(action) {
  const { zoneId, schedule } = action;

  let matchingScheduleFound = false;

  while (matchingScheduleFound === false) {
    const liveMessageAction = yield take([types.WEBSOCKET_LIVE_RECEIVED]);

    const { data } = liveMessageAction;

    if (data.home && data.home.zones) {
      const selectedZone = data.home.zones.find(x =>
        isMatchingZoneId(x.zoneId, zoneId),
      );

      const selectedSchedule =
        selectedZone?.schedule || selectedZone?.dhwSchedule;

      // protect from bad data
      if (selectedZone && selectedSchedule) {
        const currentSchedule = flattenSchedule([...selectedSchedule]);
        const result = compareSchedule(schedule, currentSchedule);
        matchingScheduleFound = result.isTheSame;
      }
    }
  }
  return matchingScheduleFound;
}

function* ensureTimedDhwEnabled(sendWsMessage, homeId, zoneId) {
  const timedDhw = yield select(getTimedDhw, homeId, zoneId);

  if (timedDhw) {
    return true;
  }
  const requestId = uuid();
  const prtSetting = 'hpiTimedDhw';

  yield put({
    type: types.PRT_SETTINGS_UPDATE_STARTED,
    homeId,
    zoneId,
    requestId,
    prtSetting,
    value: true,
  });

  const message = messages.hpiGetPrtMessage(
    homeId,
    zoneId,
    requestId,
    prtSetting,
    true,
    // deviceToken,
  );

  sendWsMessage(message);

  // trusting that this will be called in a race block
  while (true) {
    const inProgress = yield select(getIsSettingRequestInProgress, requestId);

    if (!inProgress) {
      return yield select(getTimedDhw, homeId, zoneId);
    }
    yield delay(100);
  }
}

function* waitAndClearPendingState(action) {
  // we need a longer delay here because schedules can be queued
  const totalWaitPeriod = 120000;
  yield race({
    response: call(updateScheduleCheck, action),
    timeout: delay(totalWaitPeriod),
  });

  yield put({ ...action, type: types.CLEAR_PENDING_SCHEDULE });
}

function* sendSchedule(action) {
  const { sendWsMessage, requestId, homeId, zoneId, schedule, isHpi } = action;

  const deviceToken = yield select(getDeviceToken);
  const currentSchedule = yield select(getActualSchedule, homeId, zoneId);

  const frostProtectionSetpoint = yield select(
    getFrostProtectionSetpoint,
    homeId,
    zoneId,
  );

  // Clone schedule before mutating to prevent Redux state mutation
  // The schedule may be a reference from the draft reducer state
  const mutableSchedule = schedule.map(event => ({ ...event }));

  // go through schedule and set all setpoint that is smaller than frostProtectionSetpoint into frostProtectionSetpoint
  mutableSchedule.forEach(event => {
    if (event.setpoint < frostProtectionSetpoint) {
      event.setpoint = frostProtectionSetpoint;
    }
  });

  const { numberOfDifferences, isTheSame } = compareSchedule(
    currentSchedule,
    mutableSchedule,
  );

  // if it's not changed, just pretend it went through really quickly
  if (isTheSame) {
    yield put({
      type: types.SET_SCHEDULE_CONFIRMED,
      homeId,
      zoneId,
      requestId,
      schedule: mutableSchedule,
      numberOfDifferences,
    });
    // SET_SCHEDULE_CONFIRMED marks pending as 'done', so loading stops
    // No need to wait for LIVE message since schedule hasn't changed
    yield put({ ...action, type: types.CLEAR_PENDING_SCHEDULE });
    return;
  }

  const isHpiDhw = yield select(getZoneType, homeId, zoneId) === 'HPI_DHW';
  if (isHpiDhw) {
    const { completed } = yield race({
      completed: call(ensureTimedDhwEnabled, sendWsMessage, homeId, zoneId),
      timeout: take(types.PRT_SETTINGS_UPDATE_TIMED_OUT),
    });

    if (!completed) {
      yield put({ ...action, type: types.SET_SCHEDULE_TIMED_OUT });
      return;
    }
  }

  const deviceServiceEnabled = yield select(
    isHaloDeviceServiceEnabled,
    homeId,
    zoneId,
  );

  if (deviceServiceEnabled) {
    const message = {
      action: isHpi ? 'hpiDesiredSchedule' : 'siDesiredSchedule',
      deviceToken,
      data: {
        [isHpi ? 'hpiId' : 'siId']: homeId,
        prtId: zoneId[0],
        endpointId: zoneId[1],
        requestId,
        schedule: unflattenSchedule(mutableSchedule),
      },
    };

    // this doesn't need to be a call() because it isn't async
    sendWsMessage(message);

    const { responseAck, responseNak } = yield race({
      responseAck: take(types.COMMAND_STATUS_ACK),
      responseNak: take(types.COMMAND_STATUS_NACK),
      timeout: delay(GLOBAL_TIMEOUT),
    });

    if (responseAck) {
      yield put({ ...action, type: types.SET_SCHEDULE_CONFIRMED });
      // SET_SCHEDULE_CONFIRMED marks pending as 'done', so loading stops immediately
      // Pending data is kept for optimistic UI until LIVE message confirms

      // we spawn here to make sure we're ready to accept a second schedule update
      // and verify the schedule was applied via LIVE message
      yield spawn(waitAndClearPendingState, action);
    } else if (responseNak) {
      yield put({ ...action, type: types.SET_SCHEDULE_REJECTED });
      yield put({ ...action, type: types.CLEAR_PENDING_SCHEDULE });
    } else {
      yield put({ ...action, type: types.SET_SCHEDULE_TIMED_OUT });
      yield put({ ...action, type: types.CLEAR_PENDING_SCHEDULE });
    }
  } else {
    const message = isHpi
      ? messages.hpiSetPrtSchedule(
          homeId,
          zoneId,
          requestId,
          mutableSchedule,
          deviceToken,
        )
      : messages.setPrtSchedule(
          homeId,
          zoneId,
          requestId,
          mutableSchedule,
          deviceToken,
        );

    sendWsMessage(message);

    yield put({ type: types.COMMAND_SENT, message });

    const totalWaitPeriod = numberOfDifferences * GLOBAL_TIMEOUT;
    const { response } = yield race({
      response: call(updateScheduleCheck, action),
      timeout: delay(totalWaitPeriod),
    });

    if (response) {
      yield put({ ...action, type: types.SET_SCHEDULE_CONFIRMED });
    } else {
      yield put({ ...action, type: types.SET_SCHEDULE_TIMED_OUT });
    }

    yield put({ ...action, type: types.CLEAR_PENDING_SCHEDULE });
  }
}

function* scheduleQueue() {
  while (true) {
    const action = yield take(types.SET_SCHEDULE_STARTED);
    // actual implementation moved out of the loop to prevent accidental returns
    // preventing a second schedule action from starting
    yield call(sendSchedule, action);
  }
}

// Apply one schedule to many zones. Dispatches per-zone SET_SCHEDULE_STARTED
// and waits for each zone's terminal action (matched by requestId) before
// moving on. Stops on the first failure. Emits a single batch terminal
// action so UI code can observe completion without reading intermediate
// per-zone transitions, which React batching can hide.
export function* sendSchedulesBatch(action) {
  const {
    sendWsMessage,
    homeId,
    zoneIds,
    schedule: newSchedule,
    isHpi,
    batchRequestId,
  } = action;

  for (const zoneId of zoneIds) {
    const requestId = uuid();

    // Buffer the terminal actions for this requestId before dispatching the
    // start action, since sendSchedule can emit the terminal action
    // synchronously (e.g. the isTheSame fast path).
    const terminalChannel = yield actionChannel(
      a =>
        (a.type === types.SET_SCHEDULE_CONFIRMED ||
          a.type === types.SET_SCHEDULE_REJECTED ||
          a.type === types.SET_SCHEDULE_TIMED_OUT) &&
        a.requestId === requestId,
    );

    yield put({
      type: types.SET_SCHEDULE_STARTED,
      sendWsMessage,
      homeId,
      zoneId,
      requestId,
      schedule: newSchedule,
      isHpi,
    });

    const terminalAction = yield take(terminalChannel);
    terminalChannel.close();

    if (terminalAction.type !== types.SET_SCHEDULE_CONFIRMED) {
      yield put({
        type:
          terminalAction.type === types.SET_SCHEDULE_REJECTED
            ? types.SET_SCHEDULES_BATCH_REJECTED
            : types.SET_SCHEDULES_BATCH_TIMED_OUT,
        batchRequestId,
        homeId,
        failedZoneId: zoneId,
      });
      return;
    }
  }

  yield put({
    type: types.SET_SCHEDULES_BATCH_CONFIRMED,
    batchRequestId,
    homeId,
    zoneIds,
  });
}

function* scheduleBatchQueue() {
  while (true) {
    const action = yield take(types.SET_SCHEDULES_BATCH_STARTED);
    yield call(sendSchedulesBatch, action);
  }
}

export default function* scheduleIndex() {
  yield all([
    // start the watch queue
    call(scheduleQueue),
    call(scheduleBatchQueue),
  ]);
}
